import { useState, useEffect } from 'react';
import { useAuthFetch } from '@/hooks/useAdmin';
import { Plus, Edit, Trash2, X, Loader2, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANGS = [
  { code: 'en', label: 'English' }, { code: 'de', label: 'Deutsch' },
  { code: 'es', label: 'Espanol' }, { code: 'fr', label: 'Francais' },
  { code: 'pt', label: 'Portugues' }, { code: 'zh', label: 'Zhongwen' },
  { code: 'ar', label: 'Arabic' }, { code: 'hi', label: 'Hindi' },
  { code: 'ru', label: 'Russian' }, { code: 'ja', label: 'Japanese' },
];

export default function Categories() {
  const authFetch = useAuthFetch();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name_hr: '', name_translations: {}, order: 0 });
  const [showTranslations, setShowTranslations] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadCategories(); }, []);

  async function loadCategories() {
    try {
      const res = await authFetch(`${API}/admin/categories`);
      if (res.ok) { const d = await res.json(); setCategories(d.categories || []); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  function openCreate() {
    setEditingId(null);
    setForm({ name_hr: '', name_translations: {}, order: 0 });
    setShowTranslations(false);
    setShowModal(true);
  }

  function openEdit(cat) {
    setEditingId(cat.id);
    setForm({ name_hr: cat.name_hr, name_translations: cat.name_translations || {}, order: cat.order || 0 });
    setShowTranslations(false);
    setShowModal(true);
  }

  async function saveCategory() {
    if (!form.name_hr.trim()) { toast.error('Naziv je obavezan'); return; }
    setSaving(true);
    try {
      const url = editingId ? `${API}/admin/categories/${editingId}` : `${API}/admin/categories`;
      const res = await authFetch(url, { method: editingId ? 'PUT' : 'POST', body: JSON.stringify(form) });
      if (res.ok) {
        toast.success('Spremljeno!');
        setShowModal(false);
        loadCategories();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Greska');
      }
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  }

  async function deleteCategory(id) {
    if (!window.confirm('Obrisati kategoriju?')) return;
    await authFetch(`${API}/admin/categories/${id}`, { method: 'DELETE' });
    loadCategories();
  }

  if (loading) return <div className="flex items-center justify-center h-full"><Loader2 className="animate-spin text-zinc-500" /></div>;

  return (
    <div className="p-6 max-w-4xl" data-testid="categories-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold">Kategorije</h1>
        <button onClick={openCreate} className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md" data-testid="new-category-btn">
          <Plus size={16} /> Nova kategorija
        </button>
      </div>

      {categories.length === 0 ? (
        <div className="text-center py-12 text-zinc-500">Nema kategorija</div>
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="categories-table">
            <thead>
              <tr className="border-b border-zinc-800 text-zinc-500 text-left">
                <th className="px-4 py-3 font-medium">Naziv</th>
                <th className="px-4 py-3 font-medium">Slug</th>
                <th className="px-4 py-3 font-medium">Redoslijed</th>
                <th className="px-4 py-3 font-medium w-24">Akcije</th>
              </tr>
            </thead>
            <tbody>
              {categories.map(c => (
                <tr key={c.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                  <td className="px-4 py-3 text-zinc-200">{c.name_hr}</td>
                  <td className="px-4 py-3 text-zinc-500 font-mono text-xs">{c.slug}</td>
                  <td className="px-4 py-3 text-zinc-400">{c.order}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(c)} className="p-1.5 text-zinc-500 hover:text-blue-400"><Edit size={14} /></button>
                      <button onClick={() => deleteCategory(c.id)} className="p-1.5 text-zinc-500 hover:text-red-400" data-testid={`delete-cat-${c.id}`}><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-700 rounded-lg w-full max-w-lg" data-testid="category-modal">
            <div className="flex items-center justify-between p-4 border-b border-zinc-700">
              <h2 className="text-sm font-medium">{editingId ? 'Uredi kategoriju' : 'Nova kategorija'}</h2>
              <button onClick={() => setShowModal(false)} className="p-1 text-zinc-500 hover:text-white"><X size={18} /></button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Naziv (hrvatski) *</label>
                <input type="text" value={form.name_hr} onChange={e => setForm(p => ({ ...p, name_hr: e.target.value }))} className="w-full bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-sm text-zinc-200 outline-none" data-testid="category-name-input" autoFocus />
              </div>
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Redoslijed</label>
                <input type="number" value={form.order} onChange={e => setForm(p => ({ ...p, order: parseInt(e.target.value) || 0 }))} className="w-24 bg-zinc-800 border border-zinc-700 rounded-md px-3 py-2 text-sm text-zinc-200 outline-none" />
              </div>
              <div className="border border-zinc-700 rounded-lg overflow-hidden">
                <button type="button" onClick={() => setShowTranslations(!showTranslations)} className="flex items-center justify-between w-full px-3 py-2 bg-zinc-800/50 text-xs text-zinc-400">
                  <span>Prijevodi</span>
                  {showTranslations ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </button>
                {showTranslations && (
                  <div className="p-3 space-y-2">
                    {LANGS.map(l => (
                      <div key={l.code} className="flex items-center gap-2">
                        <span className="text-xs text-zinc-500 w-8 shrink-0">{l.code.toUpperCase()}</span>
                        <input type="text" value={form.name_translations?.[l.code] || ''} onChange={e => setForm(p => ({ ...p, name_translations: { ...p.name_translations, [l.code]: e.target.value } }))} className="flex-1 bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-sm text-zinc-200 outline-none" placeholder={l.label} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-zinc-700">
              <button onClick={() => setShowModal(false)} className="px-3 py-1.5 text-sm text-zinc-400 hover:text-zinc-200">Odustani</button>
              <button onClick={saveCategory} disabled={saving} className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-md" data-testid="save-category-btn">
                {saving ? 'Spremam...' : 'Spremi'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
